import React, { useState } from "react";

import { isEqual } from "lodash";

import { useCustomerBreakdownConfigs } from "@components/data/customer-breakdown-configs";
import { MonthIntervalPicker } from "@components/month-interval-picker";
import { ControlWrapper } from "@components/reports/control-center-utilities";
import { useUser } from "@components/user-context";
import { prettyLogEvent } from "@internal/analytics";
import { Pages } from "@north-beam/nb-client-side/constants";
import { PrimaryButton } from "@shared/buttons";
import { Select } from "@shared/selects";

import { useCustomerLtvV2 } from "../hooks";

interface Props {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const Sidebar = ({ isCollapsed, onToggle }: Props) => {
  const { user } = useUser();
  const { value: breakdownConfigs } = useCustomerBreakdownConfigs();

  const { filters, firstAcquiredBounds, loading, setFilters } =
    useCustomerLtvV2();

  const [firstAcquiredMonthRange, setFirstAcquiredMonthRange] = useState(
    filters.firstAcquiredMonthRange,
  );
  const [breakdown, setBreakdown] = useState(filters.breakdown);

  const availableBreakdowns = (breakdownConfigs || []).map(({ name, key }) => ({
    label: name,
    value: key,
  }));

  const isSubmitButtonDisabled =
    loading ||
    isEqual(
      {
        firstAcquiredMonthRange: filters.firstAcquiredMonthRange,
        breakdown: filters.breakdown,
      },
      { firstAcquiredMonthRange, breakdown },
    ) ||
    !breakdown;

  return (
    <>
      {isCollapsed ? (
        <button
          type="button"
          className="btn btn-link text-muted my-2"
          onClick={() => onToggle()}
        >
          <i className="fa-regular fa-sharp fa-sliders-h"></i>
        </button>
      ) : (
        <div className="py-6">
          <div className="flex items-center justify-between pl-4 pr-1">
            <h5 className="font-bold m-0">Customize</h5>
            <button
              type="button"
              className="btn btn-link text-muted"
              onClick={() => onToggle()}
            >
              <i className="fa-regular fa-sharp fa-chevron-left"></i>
            </button>
          </div>
          <div className="px-4">
            <ControlWrapper title="Customers first acquired between...">
              <MonthIntervalPicker
                disabled={loading}
                bounds={firstAcquiredBounds}
                value={firstAcquiredMonthRange}
                onUpdate={(value) => {
                  prettyLogEvent.onChange({
                    user,
                    page: Pages.CUSTOMERS,
                    additionalActionInfo: "select-date-range",
                  });

                  setFirstAcquiredMonthRange(value);
                }}
              />
            </ControlWrapper>
            <ControlWrapper title="GROUP BY">
              <Select
                isDisabled={loading}
                onChange={(value) => {
                  const config = (breakdownConfigs || []).find(
                    (e) => e.key === value,
                  );

                  if (config) {
                    setBreakdown({ key: config.key, values: [] });

                    prettyLogEvent.onSelect({
                      user,
                      page: Pages.CUSTOMERS,
                      additionalActionInfo: "select-groupby",
                    });
                  }
                }}
                value={breakdown?.key}
                options={availableBreakdowns}
                placeholder="Add breakdown..."
                icon={<i className="fa-regular fa-sharp fa-chart-pie" />}
              />
            </ControlWrapper>
            <ControlWrapper>
              <PrimaryButton
                size="md"
                fullWidth
                isDisabled={isSubmitButtonDisabled}
                onClick={() => {
                  prettyLogEvent.onClick({
                    user,
                    page: Pages.CUSTOMERS,
                    additionalActionInfo: "submit-customize-form",
                  });

                  setFilters({
                    breakdown,
                    firstAcquiredMonthRange,
                    search: { operator: "contains", query: "" },
                    page: 1,
                  });
                }}
              >
                UPDATE REPORT
              </PrimaryButton>
            </ControlWrapper>
          </div>
        </div>
      )}
    </>
  );
};
